import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Header } from 'components/Header';
import { useAuth } from 'context/AuthContext';
import { 
  User,
  Camera,
  Upload,
  Lock,
  Edit3,
  Save
} from 'lucide-react';
import Avatar from 'components/ui/avatar';
import AvatarSelector from 'components/ui/avatar-selector';
import { updateUserOnboarding } from 'services/auth.service';
import { fetchUser } from 'services/auth.service';
import { 
  getUserById, 
  updateUserById, 
  changeUserPassword
} from 'services/profile.service';
import { getProfileImage } from 'utils/helpers';
import './styles.scss';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isEditingAvatar, setIsEditingAvatar] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<string>(
    user?.extra?.fotoDePerfil || ''
  );
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [loadingAvatar, setLoadingAvatar] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  
  // Cache do perfil carregado para evitar requisições duplicadas
  const [cachedUserData, setCachedUserData] = useState<any>(null);

  // Estados para edição de perfil
  const [profileData, setProfileData] = useState({
    name: '',
    surname: '',
    email: ''
  });

  // Estados para alteração de senha
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Load user profile data on mount
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?._id) {
        setIsInitialLoading(false);
        return;
      }

      try {
        setIsInitialLoading(true);
        const userData = await getUserById(user._id);
        
        // Cache user data to avoid duplicate requests
        setCachedUserData(userData);
        
        // Get name and surname from extra or split from name
        const firstName = userData.name || '';
        const surname = userData.extra?.sobrenome || '';

        setProfileData({
          name: firstName,
          surname: surname,
          email: userData.email || ''
        });

        // Update avatar if available
        if (userData.extra?.fotoDePerfil) {
          setSelectedAvatar(userData.extra.fotoDePerfil);
        }
      } catch (error) {
        console.error('Erro ao carregar perfil:', error);
        toast.error('Não foi possível carregar os dados do perfil', {
          position: 'top-right',
          autoClose: 3000,
        });
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadUserProfile();
  }, [user?._id]);

  const handleAvatarSelect = (avatarName: string) => {
    setSelectedAvatar(avatarName);
  };

  const handleSaveAvatar = async () => {
    if (!selectedAvatar) {
      toast.error('Selecione um avatar para continuar', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    if (!user?._id) {
      toast.error('Usuário não autenticado', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      await updateUserOnboarding(selectedAvatar, user._id);
      
      toast.success('Avatar alterado com sucesso!', {
        position: 'top-right',
        autoClose: 3000,
      });

      setIsEditingAvatar(false);
      
      // Recarrega a página para atualizar o contexto
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Erro ao atualizar avatar:', error);
      toast.error('Não foi possível atualizar o avatar. Tente novamente.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    if (!user?._id) {
      toast.error('Usuário não autenticado', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    if (!profileData.name.trim()) {
      toast.error('O nome é obrigatório', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    try {
      setLoadingProfile(true);

      // Use cached data to avoid unnecessary GET request
      const currentUserData = cachedUserData || await getUserById(user._id);

      // Update profile with new data
      const updatedData = {
        ...currentUserData,
        _id: user._id,
        name: profileData.name.trim(),
        email: profileData.email,
        extra: {
          ...currentUserData.extra,
          sobrenome: profileData.surname.trim()
        }
      };

      await updateUserById(user._id, updatedData);

      toast.success('Perfil atualizado com sucesso!', {
        position: 'top-right',
        autoClose: 3000,
      });

      // Reload page to update context
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error('Erro ao atualizar perfil:', error);
      const errorMessage = error?.response?.data?.message || 'Não foi possível atualizar o perfil. Tente novamente.';
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleSavePassword = async () => {
    if (!user?._id) {
      toast.error('Usuário não autenticado', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    // Validation
    if (!passwordData.currentPassword.trim()) {
      toast.error('Digite a senha atual', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    if (!passwordData.newPassword.trim()) {
      toast.error('Digite a nova senha', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('As senhas não coincidem', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    try {
      setLoadingPassword(true);

      await changeUserPassword(
        user._id,
        passwordData.currentPassword,
        passwordData.newPassword
      );

      toast.success('Senha alterada com sucesso!', {
        position: 'top-right',
        autoClose: 3000,
      });

      // Clear password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      console.error('Erro ao alterar senha:', error);
      const errorMessage = error?.response?.data?.message || 'Não foi possível alterar a senha. Verifique se a senha atual está correta.';
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione um arquivo de imagem válido', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('O arquivo deve ter no máximo 5MB', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    if (!user?._id) {
      toast.error('Usuário não autenticado', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    try {
      setLoadingAvatar(true);

      // Convert File to base64 data URL
      const base64Image = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve(event.target?.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Use cached data to avoid unnecessary GET request
      const currentProfile = cachedUserData || await getUserById(user._id);

      // Update profile with base64 image embedded in JSON
      const payload = {
        ...currentProfile,
        _id: user._id,
        image: {
          small: {
            url: base64Image
          }
        }
      };

      await updateUserById(user._id, payload);

      toast.success('Foto de perfil atualizada com sucesso!', {
        position: 'top-right',
        autoClose: 3000,
      });

      // Reload page to update avatar
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error('Erro ao fazer upload do avatar:', error);
      const errorMessage = error?.response?.data?.message || 'Não foi possível fazer upload da foto. Tente novamente.';
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setLoadingAvatar(false);
    }
  };

  return (
    <div className="settings-page">
      <Header />
      
      <div className="settings-container">
        <div className="settings-content">
          
          {/* Hidden file input for avatar upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleAvatarUpload}
          />
          
          {/* Seção: Alterar Avatar */}
          <div className="settings-section">
            <h2 className="section-title">
              <Camera size={20} />
              Alterar avatar
            </h2>
            
            <div className="avatar-section-content">
              <div className="current-avatar">
                <div className="avatar-display">
                  <Avatar 
                    src={getProfileImage(user)}
                    name={user?.name}
                    size="xl"
                  />
                </div>
                <div className="avatar-info">
                  <p className="avatar-name">
                    {user?.name || 'Usuário'}
                    {user?.extra?.sobrenome && ` ${user.extra.sobrenome}`}
                  </p>
                </div>
              </div>

              <div className="avatar-actions">
                <button 
                  className="btn-primary"
                  onClick={() => setIsEditingAvatar(!isEditingAvatar)}
                  disabled={loadingAvatar || isInitialLoading}
                >
                  <User size={18} />
                  {isEditingAvatar ? 'Fechar seletor' : 'Escolher avatar'}
                </button>
                <button 
                  className="btn-primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loadingAvatar || isInitialLoading}
                >
                  <Upload size={18} />
                  {loadingAvatar ? 'Enviando...' : 'Upload de foto'}
                </button>
              </div>
            </div>

            {/* Seletor de Avatar (condicional) */}
            {isEditingAvatar && (
              <div className="avatar-selector-wrapper">
                <AvatarSelector
                  selectedAvatar={selectedAvatar}
                  onSelect={handleAvatarSelect}
                  onSave={handleSaveAvatar}
                  loading={loading}
                />
              </div>
            )}
          </div>

          {/* Seção: Minha Conta */}
          <div className="settings-section">
            <h2 className="section-title">
              <Edit3 size={20} />
              Minha Conta
            </h2>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Nome</label>
                <input 
                  type="text" 
                  value={profileData.name}
                  onChange={(e) => handleProfileChange('name', e.target.value)}
                  placeholder="Digite seu nome"
                  disabled={loadingProfile || isInitialLoading}
                />
              </div>
              
              <div className="form-group">
                <label>Sobrenome</label>
                <input 
                  type="text" 
                  value={profileData.surname}
                  onChange={(e) => handleProfileChange('surname', e.target.value)}
                  placeholder="Digite seu sobrenome"
                  disabled={loadingProfile || isInitialLoading}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Email</label>
              <input 
                type="email" 
                value={profileData.email}
                onChange={(e) => handleProfileChange('email', e.target.value)}
                placeholder="Digite seu email"
                disabled={true}
              />
            </div>
          </div>

          {/* Seção: Alterar Senha */}
          <div className="settings-section">
            <h2 className="section-title">
              <Lock size={20} />
              Alterar senha
            </h2>
            
            <div className="form-group">
              <label>Senha atual</label>
              <input 
                type="password" 
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                placeholder="••••••••"
                disabled={loadingPassword || isInitialLoading}
              />
            </div>

            <div className="form-group">
              <label>Nova senha</label>
              <input 
                type="password" 
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                placeholder="••••••••"
                disabled={loadingPassword || isInitialLoading}
              />
            </div>

            <div className="form-group">
              <label>Confirmar senha</label>
              <input 
                type="password" 
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                placeholder="••••••••"
                disabled={loadingPassword || isInitialLoading}
              />
            </div>
          </div>

          {/* Botão de Salvar Alterações */}
          <div className="settings-footer">
            <button 
              className="btn-save-all"
              onClick={async () => {
                // Save profile first
                await handleSaveProfile();
                
                // If password fields are filled, save password
                if (passwordData.currentPassword && passwordData.newPassword) {
                  await handleSavePassword();
                }
              }}
              disabled={loadingProfile || loadingPassword || loadingAvatar || isInitialLoading}
            >
              <Save size={20} />
              {(loadingProfile || loadingPassword) ? 'Salvando...' : 'Salvar alterações'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
